import type { CarLoadingDetails } from "@apac/components/cars";
import {
  useLoadingProgress,
  type FlightLoadingDetails,
  type HotelLoadingDetails,
  type LoadingStep,
} from "@apac/hooks";
import {
  IconNameEnum,
  useModuleBEM,
  useTenantImages,
} from "@b2bportal/core-themes";
import { Icon, Loading } from "@b2bportal/core-ui";
import { useDeviceType } from "@b2bportal/core-utilities";
import { useI18nContext } from "@hopper-b2b/i18n";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import clsx from "clsx";
import { FlightLabel } from "./components/FlightLabel";
import styles from "./styles.module.scss";

interface LoadingScreenProps {
  loadingSteps: Array<LoadingStep>;
  isLoading: boolean;
  flightLoadingDetails?: FlightLoadingDetails;
  hotelLoadingdetails?: HotelLoadingDetails;
  carLoadingDetails?: CarLoadingDetails;
  onCancelClick?: () => void;
  className?: string;
  averageTotalLoadingMillSec?: number;
  cancelText?: string;
}

export const LoadingScreen: React.FC<LoadingScreenProps> = ({
  isLoading,
  loadingSteps,
  flightLoadingDetails,
  hotelLoadingdetails,
  carLoadingDetails,
  className,
  onCancelClick,
  averageTotalLoadingMillSec,
  cancelText,
}) => {
  const { t } = useI18nContext();
  const [block, cn] = useModuleBEM(styles, "LoadingScreen");

  const { isDesktopAndUp } = useDeviceType();

  const {
    flightLoadingMobileImage,
    flightLoadingImage,
    hotelLoadingImage,
    carLoadingImage,
  } = useTenantImages();

  let backgroundImage;
  if (hotelLoadingdetails) {
    backgroundImage = hotelLoadingImage;
  } else if (carLoadingDetails) {
    backgroundImage = carLoadingImage;
  } else {
    backgroundImage = isDesktopAndUp
      ? flightLoadingImage
      : flightLoadingMobileImage;
  }

  const {
    currentStep: { title, description },
  } = useLoadingProgress({
    loading: isLoading,
    loadingSteps,
    averageTotalLoadingMillSec,
  });

  if (!isLoading) {
    return null;
  }
  return (
    <div
      className={clsx(block, className)}
      style={{ backgroundImage: `url(${backgroundImage})` }}
      aria-modal="true"
      aria-labelledby={"loading-screen-title"}
      role="alertdialog"
      tabIndex={-1}
      ref={(el) => el?.focus()}
    >
      <VisuallyHidden role="alert">{title}</VisuallyHidden>
      <div className={cn("container")}>
        <div className={cn("loading-container")}>
          <Loading className={cn("loading")} />
          <h3 className={cn("title")} id="loading-screen-title">
            {title}
          </h3>
          {description != null && (
            <span className={cn("description")}>{description}</span>
          )}
        </div>

        {flightLoadingDetails && (
          <div className={cn("flight-container")}>
            <FlightLabel {...flightLoadingDetails.originDetails} />

            <FlightLabel
              {...flightLoadingDetails.destinationDetails}
              category="destination"
            />

            <div className={cn("info-line")}>
              <Icon className={cn("icon")} iconName={IconNameEnum.clockFace} />
              <div>{flightLoadingDetails.tripDate}</div>
            </div>

            <div className={cn("info-line")}>
              <Icon
                className={cn("icon")}
                iconName={IconNameEnum.passengerSelection}
              />
              <div>{flightLoadingDetails.passengerCounts}</div>
            </div>
          </div>
        )}

        {hotelLoadingdetails && (
          <div className={cn("hotel-container")}>
            <div className={cn("info-line")}>
              <Icon className={cn("icon")} iconName={IconNameEnum.location} />
              <div>{hotelLoadingdetails.title}</div>
            </div>

            <div className={cn("info-line")}>
              <Icon className={cn("icon")} iconName={IconNameEnum.clockFace} />
              <div>{hotelLoadingdetails.tripDate}</div>
            </div>

            <div className={cn("info-line")}>
              <Icon
                className={cn("icon")}
                iconName={IconNameEnum.passengerSelection}
              />
              <div>{hotelLoadingdetails.passengerCounts}</div>
            </div>
          </div>
        )}

        {carLoadingDetails && (
          <div className={cn("car-container")}>
            <div className={cn("info-container")}>
              <Icon
                className={cn("info-container-icon")}
                iconName={IconNameEnum.car}
              />
              <div className={cn("info-container-title")}>
                {t("apac.cars.pickup")}
              </div>
              <div className={cn("info-container-address-info")}>
                {carLoadingDetails.pickupLocation?.title}
              </div>
              <div className={cn("info-container-datetime-info")}>
                {carLoadingDetails.pickupDateTime}
              </div>
            </div>
            <div className={cn("info-container")}>
              <Icon
                className={cn("info-container-icon")}
                iconName={IconNameEnum.car}
              />
              <div className={cn("info-container-title")}>
                {t("apac.cars.dropoff")}
              </div>
              <div className={cn("info-container-address-info")}>
                {carLoadingDetails.dropoffLocation?.title}
              </div>
              <div className={cn("info-container-datetime-info")}>
                {carLoadingDetails.dropoffDateTime}
              </div>
            </div>
          </div>
        )}

        {!!onCancelClick && (
          <button onClick={onCancelClick} className={cn("cancel-button")}>
            {cancelText ?? t("cancelSearch")}
          </button>
        )}
      </div>
    </div>
  );
};
